import { Instance, types } from "mobx-state-tree";
import { currentRoomGetter, currentUserProfileGetter, servicesGetters } from "../utils";
import { UiEventListener } from "./eventHandlers";
import { ToastBarMessage, ToastBarToastType, ToastMessage, ToastMessageType } from "./toastModels";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { ChatNotificationStore } from "@fidget/common/models/mst/ui/ChatNotificationStore";
import { ReportsScope } from "../reports/ReportsScope";
import { staticConfig } from "@fidget/common/config/static-config";
import { AppPermission } from "@closerplatform/spinner-openapi";
import { DashboardView } from "../../containers/dashboard/views/DashboardView";

/**
 * Model responsible for storing various information about the state of UI
 * that affects the app across components, eg. list of toast messages that can be
 * added to from deep down the component tree
 */
export const UiStore = types.compose(
  "UiStore",
  ChatNotificationStore,
  types
    .model({
      toastBarMessage: types.maybe(ToastBarMessage),
      toastMessages: types.map(ToastMessage),
      upgradeModal: types.maybe(types.boolean),
      userflowButtonPresent: false
    })
    .views(servicesGetters)
    .views(currentRoomGetter)
    .views(currentUserProfileGetter)
    .views((self) => {
      const views = {
        canDisplayConversations(): boolean {
          return (
            views.canDisplayWithPermission(AppPermission.DisplayYoursConversations) ||
            views.canDisplayWithPermission(AppPermission.DisplayClosedConversations) ||
            views.canDisplayWithPermission(AppPermission.DisplayWaitingConversations) ||
            views.canDisplayWithPermission(AppPermission.DisplayFollowedConversations) ||
            views.canDisplayWithPermission(AppPermission.DisplayInProgressConversations)
          );
        },

        canDisplayOnlyOwnConversations(): boolean {
          return (
            views.canDisplayWithPermission(AppPermission.DisplayYoursConversations) &&
            !views.canDisplayWithPermission(AppPermission.DisplayClosedConversations) &&
            !views.canDisplayWithPermission(AppPermission.DisplayWaitingConversations) &&
            !views.canDisplayWithPermission(AppPermission.DisplayFollowedConversations) &&
            !views.canDisplayWithPermission(AppPermission.DisplayInProgressConversations)
          );
        },

        canDisplayAllConversations(): boolean {
          return (
            views.canDisplayTagGroupConversations() ||
            (views.canDisplayWithPermission(AppPermission.DisplayYoursConversations) &&
              views.canDisplayWithPermission(AppPermission.DisplayClosedConversations) &&
              views.canDisplayWithPermission(AppPermission.DisplayWaitingConversations) &&
              views.canDisplayWithPermission(AppPermission.DisplayFollowedConversations) &&
              views.canDisplayWithPermission(AppPermission.DisplayInProgressConversations))
          );
        },

        canDisplayYoursConversations(): boolean {
          return views.canDisplayWithPermission(AppPermission.DisplayYoursConversations);
        },

        canDisplayFollowedConversations(): boolean {
          return views.canDisplayWithPermission(AppPermission.DisplayFollowedConversations);
        },

        canDisplayInProgressConversations(): boolean {
          return views.canDisplayWithPermission(AppPermission.DisplayInProgressConversations);
        },

        canDisplayWaitingConversations(): boolean {
          return views.canDisplayWithPermission(AppPermission.DisplayWaitingConversations);
        },

        canDisplayClosedConversations(): boolean {
          return views.canDisplayWithPermission(AppPermission.DisplayClosedConversations);
        },

        canDisplayTagGroupConversations(): boolean {
          return views.canDisplayWithPermission(AppPermission.DisplayOwnTagGroupsConversations);
        },

        canDisplayAdminComponents(): boolean {
          return !!self.currentUserProfile?.doesHavePermission(AppPermission.AdminPermission);
        },

        canDisplayOnlineMeetings(): boolean {
          return !!self.currentUserProfile?.doesHavePermission(
            AppPermission.MeetingsPageVisibility
          );
        },

        canDisplayGuides(): boolean {
          return !!self.currentUserProfile?.doesHavePermission(AppPermission.HelpPageVisibility);
        },

        canDisplayPersonalSettings(): boolean {
          return !!self.currentUserProfile?.doesHavePermission(AppPermission.SettingsPersonal);
        },

        canUseConversationAssign(): boolean {
          return Boolean(
            self.currentUserProfile?.doesHavePermission(AppPermission.RoomAssignSelf) ||
            self.currentUserProfile?.doesHavePermission(AppPermission.RoomAssignAgent) ||
            self.currentUserProfile?.doesHavePermission(AppPermission.RoomAssignByTag) ||
            self.currentUserProfile?.doesHavePermission(AppPermission.RoomAssignByTagGroup)
          );
        },

        canDisplayReports(scope?: ReportsScope, view?: DashboardView): boolean {
          if (!scope) {
            return (
              views.canDisplayWithPermission(AppPermission.ReportsYoursEfficiency) ||
              views.canDisplayWithPermission(AppPermission.ReportsGroupsEfficiency) ||
              views.canDisplayWithPermission(AppPermission.ReportsConversationsServiceLevelTagGroups) ||
              views.canDisplayWithPermission(AppPermission.ReportsConversationsServiceLevelTags) ||
              views.canDisplayWithPermission(AppPermission.ReportsConversationsBackLineTags) ||
              views.canDisplayWithPermission(AppPermission.ReportsAdvisersAdviserGroups) ||
              views.canDisplayWithPermission(AppPermission.ReportsAdvisersTagGroups) ||
              views.canDisplayWithPermission(AppPermission.ReportsOrgOther) ||
              views.canDisplayWithPermission(AppPermission.ReportsOrgEfficiency)
            );
          } else if (scope === ReportsScope.Yours) {
            if (!view || view === DashboardView.REPORTS_EFFICIENCY) {
              return views.canDisplayWithPermission(AppPermission.ReportsYoursEfficiency);
            }
          } else if (scope === ReportsScope.Groups) {
            if (!view || view === DashboardView.REPORTS_EFFICIENCY) {
              return views.canDisplayWithPermission(AppPermission.ReportsGroupsEfficiency);
            }
          } else if (scope === ReportsScope.Conversations) {
            if (!view) {
              return (
                views.canDisplayWithPermission(AppPermission.ReportsConversationsServiceLevelTagGroups) ||
                views.canDisplayWithPermission(AppPermission.ReportsConversationsServiceLevelTags) ||
                views.canDisplayWithPermission(AppPermission.ReportsConversationsBackLineTags)
              );
            } else if (view === DashboardView.REPORTS_SERVICE_LEVEL_TAG_GROUPS) {
              return views.canDisplayWithPermission(AppPermission.ReportsConversationsServiceLevelTagGroups);
            } else if (view === DashboardView.REPORTS_SERVICE_LEVEL_TAGS) {
              return views.canDisplayWithPermission(AppPermission.ReportsConversationsServiceLevelTags);
            } else if (view === DashboardView.REPORTS_BACK_LINE_TAGS) {
              return views.canDisplayWithPermission(AppPermission.ReportsConversationsBackLineTags);
            }
          } else if (scope === ReportsScope.Advisers) {
            if (!view) {
              return (
                views.canDisplayWithPermission(AppPermission.ReportsAdvisersAdviserGroups) ||
                views.canDisplayWithPermission(AppPermission.ReportsAdvisersTagGroups)
              );
            } else if (view === DashboardView.REPORTS_ADVISER_GROUPS) {
              return views.canDisplayWithPermission(AppPermission.ReportsAdvisersAdviserGroups);
            } else if (view === DashboardView.REPORTS_ADVISER_TAG_GROUPS) {
              return views.canDisplayWithPermission(AppPermission.ReportsAdvisersTagGroups);
            }
          } else if (scope === ReportsScope.Org) {
            if (!view) {
              return (
                views.canDisplayWithPermission(AppPermission.ReportsOrgEfficiency) ||
                views.canDisplayWithPermission(AppPermission.ReportsOrgOther)
              );
            } else if (view === DashboardView.REPORTS_EFFICIENCY) {
              return views.canDisplayWithPermission(AppPermission.ReportsOrgEfficiency);
            } else if (view === DashboardView.REPORTS_OTHER) {
              return views.canDisplayWithPermission(AppPermission.ReportsOrgOther);
            }
          }
          return false;
        },

        canDisplayAdvisersSettings(): boolean {
          return (
            views.canDisplayWithPermission(AppPermission.SettingsAdvisersConfiguration) ||
            views.canDisplayWithPermission(AppPermission.SettingsAdvisersAvatar) ||
            views.canDisplayWithPermission(AppPermission.SettingsAdvisersAssignments) ||
            views.canDisplayWithPermission(AppPermission.SettingsAdvisersAutoAssignedLimit) ||
            views.canDisplayWithPermission(AppPermission.SettingsStatuses) ||
            views.canDisplayWithPermission(AppPermission.SettingsAgentGroupsConfiguration)
          );
        },

        canDisplayAdvisersConfiguration(): boolean {
          return views.canDisplayWithPermission(AppPermission.SettingsAdvisersConfiguration);
        },

        canDisplayAdvisersAssignments(): boolean {
          return views.canDisplayWithPermission(AppPermission.SettingsAdvisersAssignments);
        },

        canDisplayAdvisersAutoAssignedLimit(): boolean {
          return views.canDisplayWithPermission(AppPermission.SettingsAdvisersAutoAssignedLimit);
        },

        canDisplayAgentGroupsConfiguration(): boolean {
          return views.canDisplayWithPermission(AppPermission.SettingsAgentGroupsConfiguration);
        },

        canDisplayStatuses(): boolean {
          return views.canDisplayWithPermission(AppPermission.SettingsStatuses);
        },

        canDisplayWithPermission(permission: AppPermission): boolean {
          return !!self.currentUserProfile?.doesHavePermission(permission);
        }
      };

      return views;
    })
    .actions((self) => {
      const eventListenerName = "UiStoreEventListener";

      const actions = {
        /**
         * Add message to toast stack. UI should show this message.
         * @param {string}           message toast content
         * @param {ToastMessageType} type    type of toast
         */
        addToastMessage(message: string, type?: ToastMessageType) {
          self.toastMessages.set(
            `Toast_${Date.now().toString()}`,
            ToastMessage.create({ message, type })
          );
        },

        addInfoToast(message: string) {
          self.toastMessages.set(
            `Toast_${Date.now().toString()}`,
            ToastMessage.create({ message, type: ToastMessageType.INFO })
          );
        },

        addSuccessToast(message: string) {
          self.toastMessages.set(
            `Toast_${Date.now().toString()}`,
            ToastMessage.create({ message, type: ToastMessageType.SUCCESS })
          );
        },
        /**
         * Add error message to toast stack. UI should show this message.
         * @param {string}           message toast content
         */
        addErrorToast(message: string) {
          self.toastMessages.set(
            `Toast_${Date.now().toString()}`,
            ToastMessage.create({ message, type: ToastMessageType.DANGER })
          );
        },

        addGenericErrorToast() {
          actions.addErrorToast(intl.formatMessage("error.something"));
        },

        /**
         * Add message to full width toast bar in dashboard.
         * If there is already message present, it will replaced only if current priority is lower or equal
         * @param message toast content
         * @param priority priority of current toast
         * @param type type of toast
         */
        addToastBarMessage(
          message: string,
          type?: ToastMessageType,
          toastBarToastType?: ToastBarToastType
        ) {
          if (
            !self.toastBarMessage ||
            (toastBarToastType && toastBarToastType <= self.toastBarMessage.toastBarToastType)
          ) {
            self.toastBarMessage = ToastBarMessage.create({ message, type, toastBarToastType });
          }
        },

        /**
         * Remove message from toast stack, implies immediate destruction.
         * @param {string} key toast map key
         */
        removeToastMessage(key: string) {
          self.toastMessages.delete(key);
        },

        /**
         * Remove full width toast message in dashboard.
         */
        removeToastBarMessage() {
          self.toastBarMessage = undefined;
        },

        /**
         * Check if full width toast message in dashboard is
         * of given type and remove if true
         */
        removeToastBarMessageIfType(toastBarToastType: ToastBarToastType) {
          if (self.toastBarMessage?.toastBarToastType === toastBarToastType) {
            self.toastBarMessage = undefined;
          }
        },

        checkUserflowButtonPresent() {
          const checkDivWasCreated = () => {
            if (document.querySelector(staticConfig.userflowDivSelector)) {
              actions.setUserflowButtonPresent(true);
            }
          };

          checkDivWasCreated();
          setTimeout(checkDivWasCreated, 500);
          setTimeout(checkDivWasCreated, 5000);
        },

        setUserflowButtonPresent(value: boolean) {
          self.userflowButtonPresent = value;
        },

        /**
         * Change upgrade modal window visibility
         */
        changeUpgrade() {
          self.upgradeModal = !self.upgradeModal;
        },

        afterCreate() {
          self.eventListener.addGlobalListener(
            new UiEventListener(() => self as IUiStore, eventListenerName)
          );
        },

        beforeDestroy() {
          self.eventListener.removeGlobalListener(eventListenerName);
        }
      };

      return actions;
    })
);

export interface IUiStore extends Instance<typeof UiStore> { }
